import { useTranslation } from 'react-i18next';
import { PaymentLine, PaymentTypeItem } from './types';

interface PaymentsStepProps {
  payments: PaymentLine[];
  onChange: (payments: PaymentLine[]) => void;
  paymentTypes: PaymentTypeItem[];
  totalAmount: number;
}

const PAYMENT_ICONS: Record<string, string> = {
  cash: '💵',
  check: '📝',
  credit: '💳',
  bank: '🏦',
};

function getPaymentIcon(name: string): string {
  const lower = name.toLowerCase();
  if (lower.includes('cash') || lower.includes('מזומן')) return PAYMENT_ICONS.cash;
  if (lower.includes('check') || lower.includes('שיק')) return PAYMENT_ICONS.check;
  if (lower.includes('credit') || lower.includes('אשראי')) return PAYMENT_ICONS.credit;
  if (lower.includes('bank') || lower.includes('העברה')) return PAYMENT_ICONS.bank;
  return '💰';
}

export default function PaymentsStep({
  payments,
  onChange,
  paymentTypes,
  totalAmount,
}: PaymentsStepProps) {
  const { t } = useTranslation();

  const paidTotal = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const remaining = totalAmount - paidTotal;

  const addPayment = (pt: PaymentTypeItem) => {
    const defaultAmount = remaining > 0 ? remaining : 0;
    onChange([
      ...payments,
      {
        payment_type: pt.payment_type,
        payment_name: pt.payment_name,
        amount: defaultAmount,
      },
    ]);
  };

  const updateAmount = (index: number, amount: number) => {
    const updated = payments.map((p, i) =>
      i === index ? { ...p, amount } : p
    );
    onChange(updated);
  };

  const removePayment = (index: number) => {
    onChange(payments.filter((_, i) => i !== index));
  };

  const balanceColor =
    remaining === 0
      ? 'text-green-600 bg-green-50 border-green-200'
      : remaining > 0
        ? 'text-orange-600 bg-orange-50 border-orange-200'
        : 'text-red-600 bg-red-50 border-red-200';

  const balanceLabel =
    remaining === 0
      ? t('invoicing.fullyCovered')
      : remaining < 0
        ? `${t('invoicing.overpaid')} ₪${Math.abs(remaining).toFixed(2)}`
        : `${t('invoicing.remainingBalance')}: ₪${remaining.toFixed(2)}`;

  return (
    <div className="flex flex-col gap-4">
      {/* Payment type selector */}
      <label className="text-sm font-medium text-gray-600">
        {t('invoicing.selectPaymentType')}
      </label>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {paymentTypes.map((pt) => (
          <button
            key={pt.payment_type}
            type="button"
            onClick={() => addPayment(pt)}
            className={[
              'flex flex-col items-center gap-2 rounded-xl p-4 border-2',
              'bg-white hover:bg-blue-50 hover:border-blue-300',
              'border-gray-200 cursor-pointer transition-all duration-150',
              'focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1',
            ].join(' ')}
          >
            <span className="text-3xl" role="img" aria-hidden="true">
              {getPaymentIcon(pt.payment_name)}
            </span>
            <span className="text-sm font-medium text-gray-700">
              {pt.payment_name}
            </span>
          </button>
        ))}
      </div>

      {/* Payment lines */}
      {payments.length > 0 && (
        <div className="flex flex-col gap-3 mt-2">
          {payments.map((payment, index) => (
            <div
              key={index}
              className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
            >
              {/* Payment type badge */}
              <div className="flex items-center gap-2 min-w-[120px]">
                <span className="text-xl" role="img" aria-hidden="true">
                  {getPaymentIcon(payment.payment_name)}
                </span>
                <span className="text-sm font-semibold text-gray-700">
                  {payment.payment_name}
                </span>
              </div>

              {/* Amount input */}
              <div className="flex-1">
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={payment.amount || ''}
                  onChange={(e) =>
                    updateAmount(index, parseFloat(e.target.value) || 0)
                  }
                  placeholder={t('invoicing.paymentAmount')}
                  className="w-full rounded-xl border border-gray-300 p-3 text-lg font-medium
                    text-gray-800 focus:border-blue-400 focus:ring-2 focus:ring-blue-200
                    focus:outline-none transition-all ltr:text-left rtl:text-right"
                />
              </div>

              {/* Remove button */}
              <button
                type="button"
                onClick={() => removePayment(index)}
                className="flex-shrink-0 rounded-lg p-2 text-red-400 hover:text-red-600
                  hover:bg-red-50 transition-colors focus:outline-none focus:ring-2
                  focus:ring-red-300"
                aria-label={t('invoicing.remove')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0
                      000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0
                      100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7
                      8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0
                      00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add payment (dashed) */}
      {payments.length > 0 && (
        <button
          type="button"
          onClick={() => {
            if (paymentTypes.length > 0) addPayment(paymentTypes[0]);
          }}
          className="w-full rounded-xl border-2 border-dashed border-gray-300 p-3
            text-center text-sm font-medium text-gray-500 hover:border-blue-400
            hover:text-blue-500 hover:bg-blue-50 transition-all cursor-pointer
            focus:outline-none focus:ring-2 focus:ring-blue-300"
        >
          + {t('invoicing.addPayment')}
        </button>
      )}

      {/* Remaining balance indicator */}
      {payments.length > 0 && (
        <div
          className={`flex items-center justify-between rounded-xl border p-3 ${balanceColor}`}
        >
          <span className="text-sm font-semibold">{balanceLabel}</span>
          <span className="text-sm font-medium">
            {t('invoicing.total')}: ₪{totalAmount.toFixed(2)}
          </span>
        </div>
      )}
    </div>
  );
}
